/**
 * Author: Gowtham
 *
 * Component: ErrorFallBackComponent
 * Description:
 * - This is a error fallback component used by Error Boundary to show when a React Component has encountered an error
 *
 * Features:
 * - Send report: Used to report the error(Yet to implement)
 * - Reload: Reload the component, try to re-render the Failed component.
 */

import { Box, Button, Center, Flex, Img, Text } from '@chakra-ui/react';
import { type AxiosError, isAxiosError } from 'axios';
import { type FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import IconSvg from 'watchtower-ui/assets/images/error-icon-new.svg';
import { t } from 'watchtower-ui/locales/i18n';
import { getErrMessage } from 'watchtower-ui/utils/utilityFunctions';

type ErrorFallBack = {
  error: Error | AxiosError;
  resetErrorBoundary: () => void;
  reloadDisabled?: boolean;
  goBack?: () => void;
};

const ErrorFallBackComponent: FC<ErrorFallBack> = ({ error, resetErrorBoundary, reloadDisabled, goBack }) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const errorMsg = getErrMessage([error], null, true);
  const isBadRequest = isAxiosError(error) && error?.response?.status === 422;
  const isfilterAppliedNotFound = isAxiosError(error) && error?.response?.status === 400;

  const defaultGoBack = () => {
    setSearchParams();
    navigate(0);
  };

  const handleGoBack = goBack ?? defaultGoBack;

  return (
    <Box layerStyle="paper" m={2}>
      <Flex flexWrap="nowrap" alignItems="center" justifyContent="space-between" height="58vh">
        <Center flexGrow="0" flexShrink="0" w="100%" flexDirection="column" height="100%">
          <Img src={IconSvg} alt="Error Icon" width="70px" />
          <Text size="lg" mt={4} mb={2} textTransform="uppercase" fontWeight="bold">
            {t('common:noMatchingDataError')}
          </Text>
          <Text size="xs" mb={4} wordBreak="break-word">
            {errorMsg}
          </Text>
          <Box>
            <Button
              onClick={() => handleGoBack()}
              variant="secondary"
              m={2}
              size="sm"
              _hover={{
                bg: 'secondary.500',
              }}
              borderRadius="10px"
            >
              {t('common:goBack')}
            </Button>
            <Button
              borderRadius="10px"
              onClick={defaultGoBack}
              size="sm"
              variant="primary"
              _hover={{
                bg: 'primary.500',
              }}
              m={2}
              isDisabled={!isfilterAppliedNotFound}
            >
              {t('Reset Filters')}
            </Button>
            <Button
              borderRadius="10px"
              onClick={resetErrorBoundary}
              variant="primary"
              m={2}
              size="sm"
              _hover={{
                bg: 'primary.500',
              }}
              isDisabled={reloadDisabled ?? isBadRequest}
            >
              {t('common:reload')}
            </Button>
          </Box>
        </Center>
      </Flex>
    </Box>
  );
};

export default ErrorFallBackComponent;
